import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Copy, Pencil, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const EMPTY = { name: "", kind: "contract", description: "", default_price: "", default_deposit: "", email_text: "" };
const inputCls = "border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A] w-full";

export default function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editFor, setEditFor] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api.get("/admin/templates").then((r) => setTemplates(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/templates", { ...form, sections: [], questions: [] });
      setForm(EMPTY);
      load();
      toast.success("Template created");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const update = async (id, patch) => {
    try {
      await api.put(`/admin/templates/${id}`, patch);
      load();
      toast.success("Saved");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveFull = async (e, t) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const sections = [];
    fd.getAll("sec_heading").forEach((h, i) => {
      if (h || fd.getAll("sec_body")[i]) sections.push({ heading: h, body: fd.getAll("sec_body")[i] });
    });
    const questions = fd.getAll("q_label").filter(Boolean).map((label) => ({ label, placeholder: "" }));
    await update(t.id, {
      name: fd.get("name"), description: fd.get("description"),
      default_price: fd.get("default_price"), default_deposit: fd.get("default_deposit"),
      email_text: fd.get("email_text"), sections, questions,
    });
    setEditFor(null);
  };

  const duplicate = async (id) => {
    await api.post(`/admin/templates/${id}/duplicate`);
    load();
    toast.success("Duplicated");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template permanently?")) return;
    await api.delete(`/admin/templates/${id}`);
    load();
  };

  const visible = templates.filter((t) => showArchived || !t.archived);

  return (
    <div data-testid="admin-templates">
      <form onSubmit={add} className="bg-white border border-[#E5E5E2] p-6 mb-8 grid sm:grid-cols-2 gap-3">
        <p className="sm:col-span-2 text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A]">New Template</p>
        <input required placeholder="Template name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="template-add-name" className={inputCls} />
        <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="template-add-kind" className={inputCls}>
          <option value="contract">Contract (signature)</option>
          <option value="form">Form / Questionnaire (no signature)</option>
        </select>
        <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
        <div className="flex gap-3">
          <input placeholder="Default price" value={form.default_price} onChange={(e) => setForm({ ...form, default_price: e.target.value })} className={inputCls} />
          <input placeholder="Default deposit" value={form.default_deposit} onChange={(e) => setForm({ ...form, default_deposit: e.target.value })} className={inputCls} />
        </div>
        <button type="submit" data-testid="template-add-submit"
          className="sm:col-span-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] py-3 hover:bg-[#4A4A4A] transition-colors">
          Create Template
        </button>
      </form>

      <label className="flex items-center gap-2 text-xs text-[#7A7A7A] mb-4 cursor-pointer">
        <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[#1A1A1A]" data-testid="templates-show-archived" />
        Show archived templates
      </label>

      <div className="space-y-3">
        {visible.map((t) => (
          <div key={t.id} data-testid={`admin-template-${t.id}`} className={`bg-white border p-4 ${t.archived ? "border-[#E5E5E2] opacity-60" : "border-[#E5E5E2]"}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 ${t.kind === "contract" ? "bg-[#1A1A1A] text-white" : "bg-[#EFEFEC] text-[#4A4A4A]"}`}>
                {t.kind === "contract" ? "Contract" : "Form"}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{t.name} {t.archived && <span className="text-xs text-[#7A7A7A]">(archived)</span>}</p>
                <p className="text-xs text-[#7A7A7A] truncate">{t.description}</p>
              </div>
              <button onClick={() => setEditFor(editFor === t.id ? null : t.id)} data-testid={`template-edit-${t.id}`} className="p-2 border border-[#E5E5E2]">
                {editFor === t.id ? <X size={13} /> : <Pencil size={13} />}
              </button>
              <button onClick={() => duplicate(t.id)} data-testid={`template-duplicate-${t.id}`} className="p-2 border border-[#E5E5E2]"><Copy size={13} /></button>
              <button onClick={() => update(t.id, { archived: !t.archived })} data-testid={`template-archive-${t.id}`} className="p-2 border border-[#E5E5E2]"><Archive size={13} /></button>
              <button onClick={() => remove(t.id)} data-testid={`template-delete-${t.id}`} className="p-2 border border-[#E5E5E2] text-red-500"><Trash2 size={13} /></button>
            </div>

            {editFor === t.id && (
              <form onSubmit={(e) => saveFull(e, t)} className="mt-4 border-t border-[#E5E5E2] pt-4 space-y-3" data-testid={`template-edit-form-${t.id}`}>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input name="name" defaultValue={t.name} className={inputCls} />
                  <input name="description" defaultValue={t.description} placeholder="Description" className={inputCls} />
                  <input name="default_price" defaultValue={t.default_price} placeholder="Default price" className={inputCls} />
                  <input name="default_deposit" defaultValue={t.default_deposit} placeholder="Default deposit" className={inputCls} />
                </div>
                <textarea name="email_text" rows={2} defaultValue={t.email_text} placeholder="Custom email wording when this is sent (optional)"
                  className={`${inputCls} resize-y`} />
                {t.kind === "contract" && (
                  <div className="space-y-2" data-testid={`template-sections-${t.id}`}>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A]">Sections (edit text, add or remove)</p>
                    {(t.sections || []).map((s, i) => (
                      <div key={i} className="grid gap-2 border border-[#EFEFEC] p-3">
                        <input name="sec_heading" defaultValue={s.heading} placeholder="Section heading" className={inputCls} />
                        <textarea name="sec_body" rows={2} defaultValue={s.body} placeholder="Section text" className={`${inputCls} resize-y`} />
                      </div>
                    ))}
                    <div className="grid gap-2 border border-dashed border-[#E5E5E2] p-3">
                      <input name="sec_heading" placeholder="New section heading" className={inputCls} data-testid={`template-new-section-${t.id}`} />
                      <textarea name="sec_body" rows={2} placeholder="New section text" className={`${inputCls} resize-y`} />
                    </div>
                    <p className="text-xs text-[#7A7A7A]">To remove a section, clear both its fields.</p>
                  </div>
                )}
                {t.kind === "form" && (
                  <div className="space-y-2" data-testid={`template-questions-${t.id}`}>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A]">Questions (one per line field, clear to remove)</p>
                    {(t.questions || []).map((q, i) => (
                      <input key={i} name="q_label" defaultValue={q.label} className={inputCls} />
                    ))}
                    <input name="q_label" placeholder="Add a question…" className={inputCls} data-testid={`template-new-question-${t.id}`} />
                  </div>
                )}
                <button type="submit" data-testid={`template-save-${t.id}`}
                  className="bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] px-8 py-2.5 hover:bg-[#4A4A4A] transition-colors">
                  Save Template
                </button>
              </form>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
